import React, { useState, FormEvent, ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';

function Register(): React.ReactElement {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError(t('auth.passwordsNoMatch'));
      return;
    }
    if (password.length < 8) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await register(name, email, password);
      if (result.success) {
        navigate('/');
      } else {
        setError(result.message || t('common.error'));
      }
    } catch (err) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="text-center mb-4">
          <i className="pi pi-book text-5xl text-primary mb-3"></i>
          <h1 className="text-2xl font-bold m-0">{t('app.name')}</h1>
          <p className="text-color-secondary mt-2">{t('app.tagline')}</p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <Message severity="error" text={error} className="w-full mb-3" />}
          
          <div className="field mb-3">
            <label htmlFor="name" className="block mb-2 font-medium">{t('auth.name')}</label>
            <InputText
              id="name"
              value={name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              className="w-full"
              required
            />
          </div>

          <div className="field mb-3">
            <label htmlFor="email" className="block mb-2 font-medium">{t('auth.email')}</label>
            <InputText
              id="email"
              type="email"
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              className="w-full"
              required
            />
          </div>

          <div className="field mb-3">
            <label htmlFor="password" className="block mb-2 font-medium">{t('auth.password')}</label>
            <Password
              id="password"
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              className="w-full"
              inputClassName="w-full"
              toggleMask
              required
            />
          </div>

          <div className="field mb-4">
            <label htmlFor="confirmPassword" className="block mb-2 font-medium">{t('auth.confirmPassword')}</label>
            <Password
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
              className="w-full"
              inputClassName="w-full"
              feedback={false}
              toggleMask
              required
            />
          </div>

          <Button
            type="submit"
            label={t('auth.signUp')}
            className="w-full"
            loading={loading}
          />
        </form>

        <div className="text-center mt-4">
          <span className="text-color-secondary">{t('auth.hasAccount')} </span>
          <Link to="/login" className="text-primary font-medium no-underline">
            {t('auth.signIn')}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Register;
